import { AdaptiveDpr, PerformanceMonitor } from "@react-three/drei";
import { Canvas } from "@react-three/fiber";
import { useEffect, useMemo, useState } from "react";
import { useReducedMotion } from "../hooks/useReducedMotion";
import { useDeviceCapability } from "../lib/3d/device";
import { getCanvasDpr, getQualityPreset } from "../lib/3d/quality";
import { useWebGLSupport } from "../lib/3d/useWebGLSupport";
import { useAppStore } from "../store/useAppStore";
import { Experience } from "./Experience";
import { PCFSoftShadowMap } from "three";

export function CanvasRoot() {
  const supportsWebGL = useWebGLSupport();
  const capability = useDeviceCapability();
  const reducedMotion = useReducedMotion();
  const setReducedMotion = useAppStore((state) => state.setReducedMotion);
  const setAssetsReady = useAppStore((state) => state.setAssetsReady);
  const quality = getQualityPreset({ ...capability, prefersReducedMotion: reducedMotion });
  const dpr = useMemo(() => getCanvasDpr(quality), [quality]);
  const [performanceFactor, setPerformanceFactor] = useState(1);

  useEffect(() => {
    setReducedMotion(reducedMotion);
  }, [reducedMotion, setReducedMotion]);

  if (!supportsWebGL) {
    return null;
  }
  const shadowsEnabled = quality !== "low";

  return (
    <div aria-hidden="true" className="canvas-root">
      <Canvas
        shadows={shadowsEnabled}
        camera={{ fov: 32, near: 0.1, far: 50, position: [0, 0.28, 7.1] }}
        dpr={[dpr[0], dpr[1] * performanceFactor]}
        gl={{ alpha: true, antialias: quality !== "low", powerPreference: "high-performance" }}
        onCreated={({ gl }) => {
          gl.shadowMap.enabled = shadowsEnabled;
          gl.shadowMap.type = PCFSoftShadowMap;
          setAssetsReady(true);
        }}
      >
        <color attach="background" args={["#08111c"]} />
        <PerformanceMonitor onChange={({ factor }) => setPerformanceFactor(factor)} />
        <AdaptiveDpr />
        <Experience capability={{ ...capability, prefersReducedMotion: reducedMotion }} quality={quality} />
      </Canvas>
      <div className="canvas-vignette" />
    </div>
  );
}
